import { Module, ActionTree, MutationTree, GetterTree, Dispatch } from "vuex"
import { RootState } from "@/store/RootStore"
import { make } from "vuex-pathify"
import { StoreUtils } from "@/utils/StoreUtils"
import services from "@/services/Services"
import { AxiosError } from "axios"
import {
    AppleWalletJws,
    WALLET_PASSES_CACHE_TTL_MS,
    WALLET_PASS_DEVICE_GROUPS,
    WALLET_PASS_FAILED_GROUPS,
    WALLET_PASS_ISSUING_STATUSES,
    WALLET_PASS_LISTED_GROUPS,
    WalletConfig,
    WalletFlowStatus,
    WalletPass,
    WalletPassStatus,
    WalletType
} from "@/models/Wallet"
import {
    googleWalletSaveUrl,
    isWalletPass,
    isWalletPassExpired,
    isWalletPassRedeemable,
    isWalletType,
    walletErrorMessage,
    walletPassStatusGroup
} from "@/utils/WalletUtils"
import { WALLET_MESSAGES } from "@/utils/WalletConstants"
import { clearPendingPassId, getPendingPassId, getPendingSaveUrl, savePendingPassId } from "@/utils/WalletPendingPassStorage"

const namespaced: boolean = true
const HTTP_NOT_FOUND = 404
let latestLoadRequest = 0

function isNotFoundError(err: unknown): boolean {
    return (err as AxiosError | undefined)?.response?.status === HTTP_NOT_FOUND
}

function onWalletError(dispatch: Dispatch) {
    return async (err: unknown) => {
        await dispatch("setFlowResult", { status: WalletFlowStatus.FAILED, message: walletErrorMessage(err) })
        return true
    }
}

//===================================
// State
//===================================
export interface WalletPlatformsPayload {
    propertyId: number
    passId: string
}

export interface WalletFlowResultPayload {
    status: WalletFlowStatus
    message?: string | null
}

export interface WalletPassesCache {
    propertyId: number
    loadedAt: number
}

export interface WalletState {
    propertyId: number | null
    passesCache: WalletPassesCache | null
    passes: WalletPass[]
    config: WalletConfig | null
    supportedPlatforms: WalletType[]
    flowStatus: WalletFlowStatus
    flowMessage: string | null
    googleSaveUrl: string | null
    pendingPassId: string | null
}

function defaultState(): WalletState {
    return {
        propertyId: null,
        passesCache: null,
        passes: [],
        config: null,
        supportedPlatforms: [],
        flowStatus: WalletFlowStatus.IDLE,
        flowMessage: null,
        googleSaveUrl: null,
        pendingPassId: null
    }
}

export const state: WalletState = defaultState()

//===================================
// Getters
//===================================
export const getters: GetterTree<WalletState, RootState> = {
    ...make.getters(state),

    availablePasses(state: WalletState): WalletPass[] {
        return state.passes.filter((pass) => isWalletPassRedeemable(pass))
    },

    registeredPasses(state: WalletState): WalletPass[] {
        return state.passes.filter((pass) => {
            const group = walletPassStatusGroup(pass.status)
            return isWalletPass(pass) && pass.status !== WalletPassStatus.CREATED && group !== undefined && WALLET_PASS_LISTED_GROUPS.includes(group)
        })
    },

    hasExpiredPass(state: WalletState): boolean {
        return state.passes.some((pass) => isWalletPass(pass) && isWalletPassExpired(pass))
    },

    hasVisiblePasses(_state: WalletState, walletGetters): boolean {
        return (
            (walletGetters.availablePasses as WalletPass[]).length > 0 ||
            (walletGetters.registeredPasses as WalletPass[]).length > 0 ||
            walletGetters.hasExpiredPass
        )
    },

    availablePassCount(_state: WalletState, walletGetters): number {
        return (walletGetters.availablePasses as WalletPass[]).length
    }
}

//===================================
// Actions
//===================================
export const actions: ActionTree<WalletState, RootState> = {
    ...make.actions(state),

    async loadPasses({ dispatch, getters, state }, propertyId: number) {
        const request = ++latestLoadRequest
        const isStale = () => request !== latestLoadRequest
        await dispatch("setPropertyId", propertyId)
        await dispatch("setPassesCache", null)
        const config = state.config ?? (await services.wallet.getConfig())
        if (isStale()) return
        await dispatch("setConfig", config)
        if (!config) {
            await dispatch("setPasses", [])
            await dispatch("setSupportedPlatforms", [])
            await dispatch("setPassesCache", { propertyId, loadedAt: Date.now() })
            return
        }
        let arePassesLoaded = false
        await StoreUtils.withLoading(
            dispatch,
            async () => {
                const passes = await services.wallet.getPasses(propertyId)
                if (isStale()) return
                await dispatch("setPasses", passes)
                arePassesLoaded = true
            },
            async (err: unknown) => {
                if (isStale()) return true
                await dispatch("setPasses", [])
                arePassesLoaded = isNotFoundError(err)
                return arePassesLoaded
            }
        )
        if (isStale()) return
        const passId = (getters.availablePasses as WalletPass[])[0]?.passId
        let arePlatformsLoaded = true
        if (passId) {
            arePlatformsLoaded = await dispatch("loadPlatforms", { propertyId, passId })
        } else {
            await dispatch("setSupportedPlatforms", [])
        }
        if (arePassesLoaded && arePlatformsLoaded && !isStale()) {
            await dispatch("setPassesCache", { propertyId, loadedAt: Date.now() })
        }
    },

    async ensurePasses({ dispatch, state }, propertyId: number) {
        const cache = state.passesCache
        if (cache?.propertyId === propertyId && Date.now() - cache.loadedAt < WALLET_PASSES_CACHE_TTL_MS) return
        await dispatch("loadPasses", propertyId)
    },

    async loadPlatforms({ dispatch }, payload: WalletPlatformsPayload): Promise<boolean> {
        const request = latestLoadRequest
        let arePlatformsLoaded = false
        await StoreUtils.withLoading(
            dispatch,
            async () => {
                const platforms = await services.wallet.getPlatforms(payload.propertyId, payload.passId)
                if (request !== latestLoadRequest) return
                await dispatch("setSupportedPlatforms", platforms.filter(isWalletType))
                arePlatformsLoaded = true
            },
            async () => {
                if (request === latestLoadRequest) await dispatch("setSupportedPlatforms", [])
                return true
            }
        )
        return arePlatformsLoaded
    },

    async getAppleJws({ dispatch, getters, state }): Promise<AppleWalletJws | undefined> {
        const passId = (getters.availablePasses as WalletPass[])[0]?.passId
        if (!passId || state.propertyId === null) return undefined
        const propertyId = state.propertyId
        let jws: AppleWalletJws | undefined
        await dispatch("setPendingPassId", passId)
        await dispatch("setFlowResult", { status: WalletFlowStatus.APPLE_VALIDATION })
        await StoreUtils.withLoading(
            dispatch,
            async () => {
                const response = await services.wallet.provision(propertyId, passId, { walletType: WalletType.APPLE_WALLET })
                jws = response.appleWallet?.jws
            },
            onWalletError(dispatch)
        )
        return jws
    },

    async provisionGoogle({ dispatch, getters, state }, googleIdToken: string): Promise<string | undefined> {
        const pass = (getters.availablePasses as WalletPass[])[0]
        if (!pass || state.propertyId === null) return undefined
        const propertyId = state.propertyId
        let saveUrl: string | undefined
        await StoreUtils.withLoading(
            dispatch,
            async () => {
                const response = await services.wallet.provision(propertyId, pass.passId, { walletType: WalletType.GOOGLE_WALLET, googleIdToken })
                const blob = response.googleWallet?.blob
                saveUrl = blob ? googleWalletSaveUrl(blob) : undefined
            },
            onWalletError(dispatch)
        )
        if (saveUrl) {
            savePendingPassId(pass.passId, saveUrl)
            await dispatch("setPendingPassId", pass.passId)
            await dispatch("setGoogleSaveUrl", saveUrl)
            await dispatch("setFlowResult", { status: WalletFlowStatus.GOOGLE_VALIDATION })
        }
        return saveUrl
    },

    async restorePendingPass({ dispatch, state }, requiresSaveUrl: boolean): Promise<boolean> {
        const passId = getPendingPassId()
        const pendingPass = passId ? state.passes.find((pass) => pass.passId === passId) : undefined
        if (!pendingPass) return false
        if (isWalletPassExpired(pendingPass)) {
            await dispatch("expirePendingPass")
            return true
        }
        const saveUrl = getPendingSaveUrl()
        if (requiresSaveUrl && !saveUrl) {
            await dispatch("clearPendingPass")
            return false
        }
        await dispatch("setPendingPassId", pendingPass.passId)
        await dispatch("setGoogleSaveUrl", saveUrl)
        await dispatch("setFlowResult", { status: WalletFlowStatus.GOOGLE_VALIDATION })
        return true
    },

    async checkPendingPass({ dispatch, state }, propertyId: number): Promise<boolean> {
        if (!state.pendingPassId) return false
        let passes: WalletPass[]
        try {
            passes = await services.wallet.getPasses(propertyId)
        } catch {
            return false
        }
        await dispatch("setPasses", passes)
        const pass = passes.find((candidate) => candidate.passId === state.pendingPassId)
        if (pass && isWalletPassExpired(pass)) {
            await dispatch("expirePendingPass")
            return true
        }
        const group = pass ? walletPassStatusGroup(pass.status) : undefined
        if (group !== undefined && WALLET_PASS_DEVICE_GROUPS.includes(group)) {
            await dispatch("clearPendingPass")
            await dispatch("setFlowResult", { status: WalletFlowStatus.SUCCESS })
            return true
        }
        if (group !== undefined && WALLET_PASS_FAILED_GROUPS.includes(group)) {
            await dispatch("dismissPendingPass")
            return true
        }
        return false
    },

    async expirePendingPass({ dispatch }) {
        await dispatch("clearPendingPass")
        await dispatch("setFlowResult", { status: WalletFlowStatus.FAILED, message: WALLET_MESSAGES.TOKEN_EXPIRED })
    },

    async confirmAppleProvisioning({ dispatch, state }) {
        const pendingPassId = state.pendingPassId
        const isResolved = state.propertyId !== null && (await dispatch("checkPendingPass", state.propertyId))
        await dispatch("clearPendingPass")
        if (isResolved) return
        const pass = state.passes.find((candidate) => candidate.passId === pendingPassId)
        if (pass && WALLET_PASS_ISSUING_STATUSES.includes(pass.status as WalletPassStatus)) {
            await dispatch("setFlowResult", { status: WalletFlowStatus.PENDING, message: WALLET_MESSAGES.IN_PROGRESS })
        } else {
            await dispatch("setFlowResult", { status: WalletFlowStatus.FAILED, message: WALLET_MESSAGES.GENERIC_ERROR })
        }
    },

    async dismissPendingPass({ dispatch }) {
        await dispatch("clearPendingPass")
        await dispatch("setFlowResult", { status: WalletFlowStatus.FAILED, message: WALLET_MESSAGES.DISMISSED })
    },

    async clearPendingPass({ dispatch }) {
        clearPendingPassId()
        await dispatch("setPendingPassId", null)
    },

    async setFlowResult({ dispatch }, payload: WalletFlowResultPayload) {
        await dispatch("setFlowStatus", payload.status)
        await dispatch("setFlowMessage", payload.message ?? null)
    },

    async resetFlow({ dispatch }) {
        await dispatch("setFlowResult", { status: WalletFlowStatus.IDLE })
        await dispatch("setGoogleSaveUrl", null)
    }
}

//===================================
// Mutations
//===================================
const mutations: MutationTree<WalletState> = {
    ...make.mutations(state),
    resetState(currentState: WalletState) {
        latestLoadRequest++
        Object.assign(currentState, defaultState())
    }
}

//===================================
// Module
//===================================
export const wallet: Module<WalletState, RootState> = {
    namespaced,
    state,
    getters,
    actions,
    mutations
}
